// ===========================================
// useClampedOverflow
// ===========================================
// Shared "is this text actually truncated?" measurement for note cards.
//
// Note bodies are clamped with `-webkit-line-clamp`. Until 2026-09-15 there was
// no way to tell a clamped note from a short one — no ellipsis, and the only
// expand control was gated on there being more than 5 notes, so with a handful
// of notes a long note simply could not be read.
//
// The check must be a MEASUREMENT, not a guess. Estimating from character count
// breaks on embedded line breaks (teachers write multi-line notes), on varying
// container widths, and on responsive font sizes — all three of which occur on
// these surfaces. `scrollHeight > clientHeight` asks the browser the question
// directly.
//
// Returns a ref to attach to the clamped element, plus whether it overflows.
// While collapsed the element is clamped so the comparison is meaningful; when
// expanded there is nothing to measure, so the last known value is kept (an
// expanded element always reports no overflow, which would otherwise make the
// "Show less" control remove itself).

import { useCallback, useEffect, useRef, useState } from 'react';

export function useClampedOverflow(
  /** Re-measure when this changes (the note body). */
  content: string,
  /** While true the element is un-clamped and must not be re-measured. */
  isExpanded: boolean
) {
  const ref = useRef<HTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el || isExpanded) return;
    // A 1px tolerance: sub-pixel line heights make scrollHeight exceed
    // clientHeight by a fraction on text that is not actually clipped.
    setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
  }, [isExpanded]);

  useEffect(() => {
    measure();
  }, [measure, content]);

  // Fonts load after first paint and reflow the text, and the container width
  // changes on resize — both can turn a non-overflowing note into an
  // overflowing one after the initial measurement.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    // Guard on the INSTANCE, not the constructor. A partial ResizeObserver —
    // a jsdom stub, or an incomplete polyfill — passes a `typeof` check on the
    // global and then throws on `.observe`, taking the whole card down with it.
    // Re-measuring on resize is an enhancement; the mount measurement already
    // covers the common case, so degrade quietly rather than crash.
    let observer: ResizeObserver;
    try {
      observer = new ResizeObserver(() => measure());
      if (typeof observer.observe !== 'function') return;
      observer.observe(el);
    } catch {
      return;
    }

    return () => {
      try {
        observer.disconnect();
      } catch {
        /* same partial-implementation case as above */
      }
    };
  }, [measure]);

  return { ref, isOverflowing };
}
